import { FEATURES, type Feature } from "./features";

/**
 * The a3s features each Vision Hub `hire_purpose` key grants.
 *
 * Source of truth for the purposes themselves is the hub's
 * `config/hire_config.php`; we only enumerate the ones a3s reacts to. Keyed by
 * purpose because that is the direction the data arrives in, and a single
 * purpose may grant several features. {@link _EveryFeatureIsGranted} below is
 * what keeps {@link FEATURES} exhaustive: adding a member there fails to
 * compile until some purpose here grants it.
 */
const FEATURES_BY_HIRE_PURPOSE = {
  waste_sense: ["wastesense"],
  road_sense: ["roadsense"],
} as const satisfies Record<string, readonly Feature[]>;

/**
 * The lookup used at runtime.
 *
 * A `Map` rather than an index into {@link FEATURES_BY_HIRE_PURPOSE}, because
 * `hire_purposes` is unvalidated hub input: indexing a plain object with
 * `"constructor"` or `"toString"` would resolve to an `Object.prototype`
 * member instead of missing.
 */
const GRANTS = new Map<string, readonly Feature[]>(Object.entries(FEATURES_BY_HIRE_PURPOSE));

type GrantedFeature = (typeof FEATURES_BY_HIRE_PURPOSE)[keyof typeof FEATURES_BY_HIRE_PURPOSE][number];
type AssertNever<T extends never> = T;

/** Every feature must be granted by at least one hire purpose. */
type _EveryFeatureIsGranted = AssertNever<Exclude<Feature, GrantedFeature>>;

/**
 * The features a hire's purposes grant.
 *
 * Takes `hire_purposes` straight off a hub hire event. A hire can carry several
 * purposes at once, and the field arrives as an unvalidated array of strings -
 * a purpose added on the hub side flows through a3s with no type error - so
 * anything this map does not know about is ignored rather than rejected.
 *
 * Returned in {@link FEATURES} order, so the result does not depend on the
 * order the hub happened to list the purposes in, and a feature granted by two
 * of the hire's purposes still appears once.
 */
export function featuresForHirePurposes(purposes: readonly string[]): Feature[] {
  const granted = new Set(purposes.flatMap((purpose) => GRANTS.get(purpose) ?? []));
  return FEATURES.filter((feature) => granted.has(feature));
}
